import { create } from 'zustand';
import type { LocalSystemContent, Message, PendingMessage } from '../types';
import * as api from '../lib/api';
import { useAuthStore } from './useAuthStore';
import { loadMessageWindow, noteMessageArrival, removeMessageCacheWindows, resetMessageCacheContext, rejectMessageViewer, endMessageCacheSession } from '../lib/message-cache-session';

interface MessageState {
  messages: Record<string, Message[]>;
  hasMore: Record<string, boolean>;
  loadingMessages: Record<string, boolean>;
  pendingMessages: Record<string, PendingMessage[]>;

  setMessages: (
    channelId: string,
    messages: Message[],
    hasMore: boolean,
    loadedBefore?: number,
  ) => void;
  prependMessages: (
    channelId: string,
    pagedFrom: string,
    messages: Message[],
    hasMore: boolean,
  ) => void;
  addMessage: (
    channelId: string,
    message: Message,
    currentUserId: string | null,
    currentChannelId: string | null,
  ) => void;
  setLoadingMessages: (channelId: string, loading: boolean) => void;
  clearLocalMessages: (channelId: string) => void;
  insertLocalSystemMessage: (channelId: string, text: string | LocalSystemContent) => void;
  editMessage: (channelId: string, message: Message) => void;
  deleteMessage: (channelId: string, messageId: string, deletedAt: number) => void;
  updateReactions: (
    channelId: string,
    messageId: string,
    reactions: { emoji: string; count: number; user_ids: string[] }[],
  ) => void;
  addReactionOptimistic: (
    channelId: string,
    messageId: string,
    emoji: string,
    userId: string,
  ) => void;
  removeReactionOptimistic: (
    channelId: string,
    messageId: string,
    emoji: string,
    userId: string,
  ) => void;
  addPendingMessage: (message: PendingMessage) => void;
  ackPendingMessage: (clientMessageId: string, channelId: string, serverMessage: Message) => void;
  failPendingMessage: (clientMessageId: string, channelId: string) => void;
  removePendingMessage: (clientMessageId: string, channelId: string) => void;
  navigateAfterLeave: (channelId: string) => void;
  purgeChannels: (channelIds: string[]) => void;
  reset: () => void;

  loadMessages: (channelId: string) => Promise<void>;
  resyncMessages: (channelId: string, heldAtGapEnd: ReadonlySet<string>) => Promise<Message[]>;
  loadOlderMessages: (channelId: string) => Promise<void>;
  sendMessage: (
    channelId: string,
    content: string,
    contentType?: 'text' | 'image',
  ) => Promise<Message>;
}

let messagesResetEpoch = 0;
const messageChannelEpochs = new Map<string, number>();

interface MessageReadEpoch {
  reset: number;
  channel: number;
}

const messageReadEpoch = (channelId: string): MessageReadEpoch => ({
  reset: messagesResetEpoch,
  channel: messageChannelEpochs.get(channelId) ?? 0,
});

const isMessageReadCurrent = (channelId: string, epoch: MessageReadEpoch): boolean =>
  epoch.reset === messagesResetEpoch &&
  epoch.channel === (messageChannelEpochs.get(channelId) ?? 0);

const invalidateMessageChannel = (channelId: string): void => {
  messageChannelEpochs.set(channelId, (messageChannelEpochs.get(channelId) ?? 0) + 1);
};

interface FoldedWindow {
  messages: Message[];
  /** Whether rows from before the read's own window survived it. */
  keptOlder: boolean;
}

/**
 * Folds a page the server just served into what the channel already holds.
 *
 * The page speaks for the range it covers and for nothing else. A locally held
 * row survives only where the read cannot answer for it: one that arrived on
 * the socket while the read was in flight, or — when `keepOlder` — one that
 * predates the page's own oldest row and therefore lies before the range the
 * page reaches. Anything else the page omitted is gone from the server and is
 * dropped here too.
 *
 * What survives must still join the page into one unbroken range, so `keepOlder`
 * is for a caller that has proven the rows below the page's floor meet it.
 * Without that proof more than a window's worth of messages arrived unseen
 * between the two, and showing them stitched together would put a hole in the
 * timeline that no scroll could fill, since paging backwards starts from the
 * oldest row held.
 */
const foldWindow = (
  existing: Message[],
  incoming: Message[],
  liveCutoff: number,
  keepOlder: boolean,
): FoldedWindow => {
  const incomingIds = new Set(incoming.map((m) => m.id));
  const windowFloor = incoming.reduce(
    (min, m) => Math.min(min, m.created_at),
    Number.POSITIVE_INFINITY,
  );
  const older = keepOlder
    ? existing.filter((m) => !incomingIds.has(m.id) && m.created_at < windowFloor)
    : [];
  const newerLive = existing.filter(
    (m) => !incomingIds.has(m.id) && !m.deleted_at && m.created_at > liveCutoff,
  );
  return {
    messages: [...older, ...incoming, ...newerLive].sort((a, b) => a.created_at - b.created_at),
    keptOlder: older.length > 0,
  };
};

export const useMessageStore = create<MessageState>()((set, get) => ({
  messages: {},
  hasMore: {},
  loadingMessages: {},
  pendingMessages: {},

  purgeChannels: (channelIds) => {
    channelIds = removeMessageCacheWindows(channelIds);
    set((s) => {
      const removed = new Set(channelIds);
      const messages = { ...s.messages };
      const hasMore = { ...s.hasMore };
      const loadingMessages = { ...s.loadingMessages };
      const pendingMessages = { ...s.pendingMessages };
      for (const id of removed) {
        invalidateMessageChannel(id);
        delete messages[id];
        delete hasMore[id];
        delete loadingMessages[id];
        delete pendingMessages[id];
      }
      return { messages, hasMore, loadingMessages, pendingMessages };
    });
  },

  setMessages: (channelId, incomingMessages, hasMore, loadedBefore) =>
    set((s) => {
      const msgs = { ...s.messages };
      const hm = { ...s.hasMore };
      const newestIncomingAt = incomingMessages.reduce(
        (max, m) => Math.max(max, m.created_at),
        Number.NEGATIVE_INFINITY,
      );
      const liveCutoff =
        loadedBefore ?? (incomingMessages.length > 0 ? newestIncomingAt : Number.POSITIVE_INFINITY);
      const folded = foldWindow(s.messages[channelId] ?? [], incomingMessages, liveCutoff, false);
      msgs[channelId] = folded.messages;
      hm[channelId] = hasMore;
      return { messages: msgs, hasMore: hm };
    }),

  /**
   * Joins a page read from before `pagedFrom` onto the range that row belongs to.
   *
   * A reconnect re-read can raise a channel's floor while this read is in
   * flight, and what comes back then borders a row the channel no longer holds:
   * joining it on would put back the hole the re-read raised the floor to avoid,
   * and its `hasMore` would answer for a floor that is no longer there — a page
   * reporting nothing before it would declare a timeline that starts mid-channel
   * complete. So the page is applied only while the row it was cursored from is
   * still the oldest held, and let go otherwise; the floor the re-read left
   * behind is still there to scroll back from.
   */
  prependMessages: (channelId, pagedFrom, newMessages, hasMore) =>
    set((s) => {
      const existing = s.messages[channelId] ?? [];
      if (existing[0]?.id !== pagedFrom) return s;
      const msgs = { ...s.messages };
      const hm = { ...s.hasMore };
      const existingIds = new Set(existing.map((m) => m.id));
      const deduped = newMessages.filter((m) => !existingIds.has(m.id));
      msgs[channelId] = [...deduped, ...existing];
      hm[channelId] = hasMore;
      return { messages: msgs, hasMore: hm };
    }),

  addMessage: (channelId, message, currentUserId, currentChannelId) => {
    noteMessageArrival(channelId, message.id);
    set((s) => {
      const msgs = { ...s.messages };
      const existing = msgs[channelId] ?? [];
      if (existing.some((m) => m.id === message.id)) return s;
      msgs[channelId] = [...existing, message];

      const isOwnMessage = !!currentUserId && message.sender_id === currentUserId;

      let pendingMessages = s.pendingMessages;
      if (isOwnMessage) {
        const chPending = s.pendingMessages[channelId];
        if (chPending && chPending.length > 0) {
          const idx = chPending.findIndex(
            (p) => p.status === 'pending' && p.content === message.content,
          );
          if (idx !== -1) {
            const nextList = chPending.slice(0, idx).concat(chPending.slice(idx + 1));
            pendingMessages = { ...s.pendingMessages };
            if (nextList.length === 0) delete pendingMessages[channelId];
            else pendingMessages[channelId] = nextList;
          }
        }
      }

      // Cross-store updates for channels/dmChannels happen via the caller (useWebSocket)
      return { messages: msgs, pendingMessages };
    });
  },

  setLoadingMessages: (channelId, loading) =>
    set((s) => {
      const l = { ...s.loadingMessages };
      if (loading) l[channelId] = true;
      else delete l[channelId];
      return { loadingMessages: l };
    }),

  clearLocalMessages: (channelId) =>
    set((s) => {
      const msgs = { ...s.messages };
      msgs[channelId] = [];
      return { messages: msgs };
    }),

  insertLocalSystemMessage: (channelId, text) =>
    set((s) => {
      const msgs = { ...s.messages };
      const existing = msgs[channelId] ?? [];
      const systemMsg: Message = {
        id: `local-${Date.now()}-${Math.random()}`,
        channel_id: channelId,
        sender_id: 'system',
        content: typeof text === 'string' ? text : '',
        ...(typeof text === 'string' ? {} : { localContent: text }),
        content_type: 'text',
        reply_to_id: null,
        created_at: Date.now(),
        edited_at: null,
      };
      msgs[channelId] = [...existing, systemMsg];
      return { messages: msgs };
    }),

  editMessage: (channelId, message) =>
    set((s) => {
      const msgs = { ...s.messages };
      const channelMsgs = msgs[channelId];
      if (!channelMsgs) return s;
      msgs[channelId] = channelMsgs.map((m) => (m.id === message.id ? { ...m, ...message } : m));
      return { messages: msgs };
    }),

  deleteMessage: (channelId, messageId, deletedAt) =>
    set((s) => {
      const msgs = { ...s.messages };
      const channelMsgs = msgs[channelId];
      if (!channelMsgs) return s;
      msgs[channelId] = channelMsgs.map((m) =>
        m.id === messageId ? { ...m, content: '', deleted_at: deletedAt } : m,
      );
      return { messages: msgs };
    }),

  updateReactions: (channelId, messageId, reactions) =>
    set((s) => {
      const msgs = { ...s.messages };
      const channelMsgs = msgs[channelId];
      if (!channelMsgs) return s;
      msgs[channelId] = channelMsgs.map((m) => (m.id === messageId ? { ...m, reactions } : m));
      return { messages: msgs };
    }),

  addReactionOptimistic: (channelId, messageId, emoji, userId) =>
    set((s) => {
      const msgs = { ...s.messages };
      const channelMsgs = msgs[channelId];
      if (!channelMsgs) return s;
      const updated = channelMsgs.map((m) => {
        if (m.id !== messageId) return m;
        const reactions = m.reactions ?? [];
        const idx = reactions.findIndex((r) => r.emoji === emoji);
        if (idx === -1) {
          return { ...m, reactions: [...reactions, { emoji, count: 1, user_ids: [userId] }] };
        }
        const existing = reactions[idx]!;
        if (existing.user_ids.includes(userId)) return m;
        const next = reactions.slice();
        next[idx] = {
          emoji: existing.emoji,
          count: existing.count + 1,
          user_ids: [...existing.user_ids, userId],
        };
        return { ...m, reactions: next };
      });
      msgs[channelId] = updated;
      return { messages: msgs };
    }),

  removeReactionOptimistic: (channelId, messageId, emoji, userId) =>
    set((s) => {
      const msgs = { ...s.messages };
      const channelMsgs = msgs[channelId];
      if (!channelMsgs) return s;
      const updated = channelMsgs.map((m) => {
        if (m.id !== messageId) return m;
        const reactions = m.reactions ?? [];
        const idx = reactions.findIndex((r) => r.emoji === emoji);
        if (idx === -1) return m;
        const existing = reactions[idx]!;
        if (!existing.user_ids.includes(userId)) return m;
        const remainingUserIds = existing.user_ids.filter((id) => id !== userId);
        if (remainingUserIds.length === 0) {
          return { ...m, reactions: reactions.filter((_, i) => i !== idx) };
        }
        const next = reactions.slice();
        next[idx] = {
          emoji: existing.emoji,
          count: Math.max(0, existing.count - 1),
          user_ids: remainingUserIds,
        };
        return { ...m, reactions: next };
      });
      msgs[channelId] = updated;
      return { messages: msgs };
    }),

  addPendingMessage: (message) =>
    set((s) => {
      const pm = { ...s.pendingMessages };
      pm[message.channelId] = [...(pm[message.channelId] ?? []), message];
      return { pendingMessages: pm };
    }),

  ackPendingMessage: (clientMessageId, channelId, serverMessage) => {
    noteMessageArrival(channelId, serverMessage.id);
    set((s) => {
      const pending = s.pendingMessages[channelId];
      if (!pending?.some((message) => message.clientMessageId === clientMessageId)) return s;
      const pm = { ...s.pendingMessages };
      const list = pending.filter((p) => p.clientMessageId !== clientMessageId);
      if (list.length === 0) delete pm[channelId];
      else pm[channelId] = list;

      const msgs = { ...s.messages };
      const existing = msgs[channelId] ?? [];
      if (!existing.some((m) => m.id === serverMessage.id)) {
        msgs[channelId] = [...existing, serverMessage];
      }

      // Cross-store channel updates happen via the caller
      return { pendingMessages: pm, messages: msgs };
    });
  },

  failPendingMessage: (clientMessageId, channelId) =>
    set((s) => {
      const pm = { ...s.pendingMessages };
      const list = (pm[channelId] ?? []).map((p) =>
        p.clientMessageId === clientMessageId ? { ...p, status: 'failed' as const } : p,
      );
      pm[channelId] = list;
      return { pendingMessages: pm };
    }),

  removePendingMessage: (clientMessageId, channelId) =>
    set((s) => {
      const pm = { ...s.pendingMessages };
      const list = (pm[channelId] ?? []).filter((p) => p.clientMessageId !== clientMessageId);
      if (list.length === 0) delete pm[channelId];
      else pm[channelId] = list;
      return { pendingMessages: pm };
    }),

  navigateAfterLeave: (channelId) => get().purgeChannels([channelId]),

  loadMessages: async (channelId) => {
    await loadMessageWindow(channelId);
  },

  resyncMessages: (channelId, heldAtGapEnd) =>
    loadMessageWindow(channelId, { resync: true, held: heldAtGapEnd }),

  loadOlderMessages: async (channelId) => {
    const epoch = messageReadEpoch(channelId);
    const s = get();
    if (s.loadingMessages[channelId]) return;
    const existing = s.messages[channelId];
    if (!existing || existing.length === 0) return;
    const oldest = existing[0]!;
    const userId = useAuthStore.getState().currentUser?.id ?? null;
    s.setLoadingMessages(channelId, true);
    try {
      const { messages, has_more, viewer_id } = await api.fetchMessages(channelId, {
        before: oldest.created_at,
        beforeId: oldest.id,
        limit: 50,
      });
      if (!isMessageReadCurrent(channelId, epoch)) return;
      if (viewer_id !== undefined && viewer_id !== userId) {
        rejectMessageViewer(userId ?? '', viewer_id);
        return;
      }
      get().prependMessages(channelId, oldest.id, messages, has_more);
    } catch (error) {
      if (!isMessageReadCurrent(channelId, epoch)) return;
      if (error instanceof api.ApiError && error.status === 401) { endMessageCacheSession(); return; }
      if (error instanceof api.ApiError && (error.status === 403 || error.status === 404)) get().purgeChannels([channelId]);
      throw error;
    } finally {
      if (isMessageReadCurrent(channelId, epoch)) get().setLoadingMessages(channelId, false);
    }
  },

  sendMessage: async (channelId, content, contentType = 'text') => {
    return api.sendMessage(channelId, content, contentType);
  },

  reset: () => {
    resetMessageCacheContext();
    messagesResetEpoch++;
    messageChannelEpochs.clear();
    set({ messages: {}, hasMore: {}, loadingMessages: {}, pendingMessages: {} });
  },
}));
